import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import _ from 'lodash';
import moment from 'moment';
import { EditorComponent } from 'ngx-monaco-editor-v2';

import { AutomationFunction } from 'src/models';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { AutomationsService } from 'src/services/automations.service';
import { MessageService } from 'src/services/message.service';

import { ActionDropdownType, ActionDropdownComponent } from '../action-dropdown/action-dropdown.component';

@Component({
  selector: 'app-edit-automation-functions',
  templateUrl: './edit-automation-functions.component.html',
  styleUrls: ['./edit-automation-functions.component.scss'],
})
export class EditAutomationFunctionsComponent implements OnInit {
  @ViewChild('editor') editor: EditorComponent;

  isLoading = false;
  isPublishing = false;
  updatedAt?: Date;
  code = 'export async function MyFunctionName({ input, unthreadApiClient, state, secrets }) {\n\t// code here\n}';
  originalCode = this.code;
  secrets: any[] = [];
  functionsVersions: AutomationFunction[];
  selectedFunction: AutomationFunction;
  selectedTab = 'properties';

  projectId: string | null = null;

  inputProperties = [
    {
      display: 'Conversation',
      key: 'input.conversation',
      model: 'Conversation',
      docs: 'https://docs.unthread.io/api-introduction/using-api#conversation',
      properties: [
        {
          display: 'ID',
          key: 'input.conversation.id',
        },
        {
          display: 'Short ID',
          key: 'input.conversation.friendlyId',
        },
        {
          display: 'Title',
          key: 'input.conversation.title',
        },
        {
          display: 'Status',
          key: 'input.conversation.status',
        },
        {
          display: 'Initial message text',
          key: 'input.conversation.initialMessage.text',
        },
        {
          display: 'Priority',
          key: 'input.conversation.priority',
        },
        {
          display: 'Ticket type ID',
          key: 'input.conversation.ticketTypeId',
        },
        {
          display: 'Ticket type fields',
          key: 'input.conversation.ticketTypeFields',
        },
        {
          display: 'Assignee ID',
          key: 'input.conversation.assignedToUserId',
        },
        {
          display: 'Source Type',
          key: 'input.conversation.sourceType',
        },
        {
          display: 'Customer ID',
          key: 'input.conversation.customerId',
        },
      ],
    },
    {
      display: 'Submitter User',
      key: 'input.conversation.submitterUser',
      model: 'User',
      docs: 'https://docs.unthread.io/api-introduction/using-api#user',
      properties: [
        {
          display: 'ID',
          key: 'input.conversation.submitterUser.id',
        },
        {
          display: 'Email',
          key: 'input.conversation.submitterUser.email',
        },
        {
          display: 'External ID',
          key: 'input.conversation.submitterUser.idpId',
        },
      ],
    },
    {
      display: 'Assigned to User',
      key: 'input.conversation.assignedToUser',
      model: 'User',
      docs: 'https://docs.unthread.io/api-introduction/using-api#user',
      properties: [
        {
          display: 'ID',
          key: 'input.conversation.assignedToUser.id',
        },
        {
          display: 'Email',
          key: 'input.conversation.assignedToUser.email',
        },
        {
          display: 'External ID',
          key: 'input.conversation.assignedToUser.idpId',
        },
      ],
    },
    {
      display: 'Old Values',
      key: 'input.oldValues',
      properties: [
        {
          display: 'Old Status',
          key: 'input.oldValues.status',
        },
        {
          display: 'Old Assignee ID',
          key: 'input.oldValues.assignedToUserId',
        },
        {
          display: 'Old Tag IDs',
          key: 'input.oldValues.tagIds',
        },
      ],
    },
    {
      display: 'New Values',
      key: 'input.newValues',
      properties: [
        {
          display: 'New Status',
          key: 'input.newValues.status',
        },
        {
          display: 'New Assignee ID',
          key: 'input.newValues.assignedToUserId',
        },
        {
          display: 'New Tag IDs',
          key: 'input.newValues.tagIds',
        },
      ],
    },
    {
      display: 'Approver',
      key: 'input.approver',
      properties: [
        {
          display: 'ID',
          key: 'input.approver.id',
        },
        {
          display: 'Name',
          key: 'input.approver.name',
        },
        {
          display: 'Email',
          key: 'input.approver.email',
        },
        {
          display: 'External ID',
          key: 'input.approver.idpId',
        },
      ],
    },
    {
      display: 'Approval Request',
      key: 'input.approvalRequest',
      properties: [
        {
          display: 'ID',
          key: 'input.approvalRequest.id',
        },
        {
          display: 'Title',
          key: 'input.approvalRequest.title',
        },
        {
          display: 'Status',
          key: 'input.approvalRequest.status',
        },
        {
          display: 'Notes',
          key: 'input.approvalRequest.notes',
        },
        {
          display: 'Assigned To User ID',
          key: 'input.approvalRequest.assignedToUserId',
        },
        {
          display: 'Assigned To Group ID',
          key: 'input.approvalRequest.assignedToGroupId',
        },
      ],
    },
    {
      display: 'Message',
      key: 'input.message',
      model: 'Message',
      docs: 'https://docs.unthread.io/api-introduction/using-api#message',
      properties: [
        {
          display: 'ID',
          key: 'input.message.id',
        },
        {
          display: 'Conversation ID',
          key: 'input.message.ticketId',
        },
        {
          display: 'User ID',
          key: 'input.message.userId',
        },
        {
          display: 'Content',
          key: 'input.message.fullContent',
        },
        {
          display: 'Slack thread ID',
          key: 'input.message.threadTs',
        },
        {
          display: 'Slack blocks',
          key: 'input.message.blocks',
        },
        {
          display: 'Source',
          key: 'input.message.source',
        },
        {
          display: 'Metadata',
          key: 'input.message.metadata',
        },
      ],
    },
    {
      display: 'Input Fields (for Manually Triggered)',
      key: 'input',
      properties: [
        {
          display: 'Custom fields',
          key: 'input.custom',
        },
      ],
    },
    {
      display: 'Webhook',
      key: 'input',
      properties: [
        {
          display: 'Headers',
          key: 'input.headers',
        },
        {
          display: 'Body',
          key: 'input.body',
        },
        {
          display: 'Query params',
          key: 'input.query',
        },
      ],
    },
  ];

  editorOptions = {
    theme: 'vs-dark',
    language: 'typescript',
    automaticLayout: true,
    fontSize: 13,
  };

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private msgSrvc: MessageService,
    private apiService: ApiService,
    private automationsService: AutomationsService,
    private alertCtrl: AlertController,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit() {
    try {
      this.initializeFunctionData();
      this.getAutomationSecrets();
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  async initializeFunctionData() {
    await this.getFunctionsVersions();

    const funcToUse = this.getFunctionsVersion('draft') ?? this.getFunctionsVersion('active');
    if (funcToUse) {
      await this.getFunction(funcToUse.version);
    }
  }

  async getFunction(version: number) {
    this.selectedFunction = await this.apiService.getPromise(`/automations/functions/${version}`, {
      projectId: this.projectId,
    });

    if (this.selectedFunction?.code) {
      this.code = this.selectedFunction.code;
      this.originalCode = this.selectedFunction.code;
    }
  }

  async getFunctionsVersions() {
    try {
      const functionsVersionsResponse: any = await this.automationsService.getAllFunctionsVersions(this.projectId);

      this.functionsVersions = functionsVersionsResponse;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getFunctionsVersion(status: string) {
    return (this.functionsVersions ?? []).find((version) => version.status === status);
  }

  async getAutomationSecrets() {
    try {
      const secretsResponse = await this.automationsService.getSecretNames(this.projectId);

      this.secrets = secretsResponse.map((secret) => {
        return {
          name: secret,
        };
      });
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async promoteDraft(didConfirm = false) {
    if (!didConfirm) {
      const confirm = await this.alertCtrl.create({
        header: `Publish this version?`,
        message: 'Are you sure you want to publish? This will replace the current active version.',
        buttons: [
          {
            text: 'No',
            handler: () => {},
          },
          {
            text: 'Yes',
            handler: () => {
              this.promoteDraft(true);
            },
          },
        ],
      });

      await confirm.present();

      return;
    }

    this.isPublishing = true;

    try {
      await this.apiService.postPromise(
        `/automations/functions/${this.selectedFunction.version}/activate`,
        {},
        {
          projectId: this.projectId,
        },
      );
      await this.getFunctionsVersions();
      this.selectedFunction = this.getFunctionsVersion('active');

      await this.msgSrvc.show('Your changes are now live!');
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isPublishing = false;
    }
  }

  async save(dismiss?: boolean) {
    this.isLoading = true;

    try {
      this.selectedFunction = await this.apiService.postPromise(
        `/automations/functions`,
        {
          code: this.code,
        },
        {
          projectId: this.projectId,
        },
      );

      this.originalCode = this.code;

      if (!dismiss) {
        this.getFunctionsVersions();
      } else {
        await this.msgSrvc.show('Updated');
        this.dismiss();
      }
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  dismiss(confirmed = false) {
    if (!confirmed && this.code !== this.originalCode) {
      this.alertCtrl
        .create({
          header: 'Unsaved changes',
          message: 'Are you sure you want to discard your changes?',
          buttons: [
            {
              text: 'No',
              role: 'cancel',
            },
            {
              text: 'Yes',
              handler: () => {
                this.dismiss(true);
              },
            },
          ],
        })
        .then((alert) => {
          alert.present();
        });

      return;
    }

    this.modalCtrl.dismiss();
  }

  copyHTTPRequestStep(method: string) {
    const text = `const response = await fetch(INSERT_URL_HERE, {
        method: '${method}',
        headers: {
            'Content-Type': 'application/json',
        },${['POST', 'PUT', 'PATCH'].includes(method) ? `\n        body: JSON.stringify({\n            // data here\n        }),` : ''}
    });`;

    this.copyToClipboard(text);
  }

  copyUnthreadApiCreateMessageStep() {
    this
      .copyToClipboard(`// Documentation available here: https://docs.unthread.io/api-introduction/using-api#create-message-in-conversation
    await unthreadApiClient.post('/conversations/INSERT_CONVERSATION_ID_HERE/messages', {
        markdown: 'Insert message content here (_markdown_ supported)',
    });`);
  }

  copyPersistentStoreCode(getOrSet: 'get' | 'set') {
    this.copyToClipboard(`await state.${getOrSet}(['YOUR_KEY_NAME_HERE'])`);
  }

  copyAddToOktaStep() {
    this.copyToClipboard(`
      const response = await fetch('INSERT_OKTA_URL/groups/INSERT_OKTA_GROUP/users/INSERT_OKTA_USER_ID', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'SSWS INSERT_OKTA_API_KEY_HERE',
          },
        });

        if (response.ok) {
          console.log('Okta user added to group');
        } else {
          console.error('Error adding Okta user to group', response.statusText, await response.text());
        }
      `);
  }

  copyRemoveFromOktaStep() {
    this.copyToClipboard(`
      const response = await fetch('INSERT_OKTA_URL/groups/INSERT_OKTA_GROUP/users/INSERT_OKTA_USER_ID', {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'SSWS INSERT_OKTA_API_KEY_HERE',
          },
        });

        if (response.ok) {
          console.log('Okta user added to group');
        } else {
          console.error('Error adding Okta user to group', response.statusText, await response.text());
        }
      `);
  }

  copyToClipboard(propertyName: string) {
    navigator.clipboard
      .writeText(propertyName)
      .then(() => {
        this.msgSrvc.show('Copied to clipboard');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  displayDateFrom(date: Date) {
    return moment(date).fromNow();
  }

  onEditorInit(_editor) {
    const monaco = (window as any).monaco;

    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ESNext,
      allowNonTsExtensions: true,
      module: monaco.languages.typescript.ModuleKind.ESNext,
      skipLibCheck: true,
    });
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      diagnosticCodesToIgnore: [2792],
    });
  }

  async showVersions() {
    const options: ActionDropdownType[] = this.functionsVersions.map((v) => {
      const emoji = v.status === 'draft' ? '🟠' : v.status === 'inactive' ? '⚪️' : '🟢';
      return {
        label: `${emoji} Version ${v.version} (${v.status})`,
      };
    });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (idx: number) => {
          popover.dismiss();
          this.selectedFunction = this.functionsVersions[idx];
          this.getFunction(this.selectedFunction.version);
        },
      },
      showBackdrop: false,
      side: 'bottom',
      alignment: 'center',
      event,
    });
    popover.present();
  }

  addFunction() {
    this.code = `${this.code}\n
export async function MyNewFunction${this.selectedFunction.exportedFunctionNames?.length}({ input, unthreadApiClient, state, secrets }) {
  // Add your code here!
}
`;
  }

  addExternalSelectFunction() {
    if (!this.code.includes('import { functions } from "npm:@unthread-io/')) {
      this.code = `import { functions } from "npm:@unthread-io/node@0.0.17";\n${this.code}`;
    }
    this.code = `${this.code}\n
export const externalSelectSample = functions.externalSelect(async ({ input, unthreadApiClient, state, secrets }) => {
	return [
		{
			label: 'Sample Option 1',
			value: 'sample-value-1'
		},
		{
			label: 'Sample Option 2',
			value: 'sample-value-2'
		}
	];
});
`;
  }
}
